import { useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";

import moment from "moment";

import { LinearProgress } from "../../application/LinearProgress";
import { RegistrationFollowUpTaskDocument } from "../../task/task.model";
import { ErrorPage } from "../../application/ErrorPage";
import { useTranslation } from "../../internationalisation/translation.hook";
import { RegistrationTaskDetailPage } from "./RegistrationTaskDetailPage";
import { TaskResponseData, useTaskResponseAction } from "../../task/task-response-action.hook";
import { useRegistrationHistory } from "../../registration/overview/registrations.hook";
import { useGardeningTask } from "../../gardening-task/gardener-view/gardening-tasks-gardener-view.hook";
import { useUser } from "../../user/UserProvider";

export interface IntegratedRegistrationTaskDetailPageProps {
    title: JSX.Element;
    task: RegistrationFollowUpTaskDocument;
}

export const IntegratedRegistrationTaskDetailPage = (props: IntegratedRegistrationTaskDetailPageProps) => {
    const { title, task } = props;
    const { t } = useTranslation();
    
    const gardener = useUser();
    const gardeningTask = useGardeningTask(task.gardenerId, task.gardeningTaskId);

    const appointment = useMemo(() => {
        if ( gardeningTask === undefined ) return undefined;
        if ( gardeningTask === null ) return null;

        if ( gardeningTask.working.visits.length === 0 ) return null;
        
        const lastVisit = gardeningTask.working.visits[gardeningTask.working.visits.length - 1];
        if ( lastVisit.appointment.status !== "open" ) return null;

        return lastVisit.appointment;
    }, [gardeningTask]);

    const history = useHistory();
    const redirect = useCallback((action: TaskResponseData["action"]) => {
        switch ( action ) {
            case "update appointment": return history.push(`/overview/redirects/registration-task/${task.id}?status=performed`);
            case "cancel appointment": return history.push(`/overview/redirects/appointment-task/${task.customerId}?status=active`);
            case "reject task because gardener says customer does not need help": return history.push(`/overview/redirects/appointment-task/${task.customerId}?status=deleted`);;
            case "reject task because gardener says customer might need help": return history.push(`/overview/redirects/appointment-task/${task.customerId}?status=deleted`);;
            case "reject task because gardener could not help customer": return history.push(`/overview/redirects/appointment-task/${task.customerId}?status=deleted`);;
            case "create registration": return history.push(`/overview/redirects/registration-task/${task.id}?status=performed`);
        }
    }, [history, task]);
    const taskResponseAction = useTaskResponseAction(redirect);

    const now = useRef(moment().toISOString(true)).current;

    const registrationHistory = useRegistrationHistory(task.gardenerId, task.customerId, now);

    if ( task.hasBeenPerformed ) return <ErrorPage message={t("OverviewTaskDetailPage: task-has-already-been-performed")} />;

    if ( gardeningTask === null || appointment === null || registrationHistory === null ) return <ErrorPage message={t("OverviewTaskDetailPage: task-could-not-be-found")} />;
    if ( gardeningTask === undefined || appointment === undefined || registrationHistory === undefined ) return <LinearProgress />;

    return (
        <>
            {title}

            <RegistrationTaskDetailPage
                gardener={gardener}
                task={task}
                gardeningTask={gardeningTask}
                appointment={appointment}
                hasHistory={registrationHistory.hasHistory}
                hasRecentHistory={registrationHistory.hasRecentHistory}
                now={now}
                taskResponseAction={taskResponseAction}
            />
        </>
    );
};
